import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Chip,
  Avatar,
} from '@mui/material';
import {
  WhatsApp,
  CheckCircle,
  Error,
  Schedule,
  QrCode,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * Sistema de notificações para WhatsApp
 * Integra com react-toastify seguindo padrões do projeto
 */

// Componente customizado para toast de status de conexão
const ConnectionStatusToast = ({ data }) => {
  const theme = useTheme();
  
  const getStatusConfig = (status) => {
    switch (status) {
      case 'open':
        return {
          icon: <CheckCircle sx={{ color: theme.palette.success.main }} />,
          chipColor: 'success',
          title: 'WhatsApp Conectado!',
        };
      case 'close':
        return {
          icon: <Error sx={{ color: theme.palette.error.main }} />,
          chipColor: 'error',
          title: 'WhatsApp Desconectado',
        };
      case 'connecting':
        return {
          icon: <Schedule sx={{ color: theme.palette.warning.main }} />,
          chipColor: 'warning',
          title: 'Conectando WhatsApp...',
        };
      case 'failed':
        return {
          icon: <Error sx={{ color: theme.palette.error.main }} />,
          chipColor: 'error',
          title: 'Falha na Conexão',
        };
      default:
        return {
          icon: <WhatsApp sx={{ color: theme.palette.info.main }} />,
          chipColor: 'info',
          title: 'Status Atualizado',
        };
    }
  };

  const config = getStatusConfig(data.status);

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Avatar
        sx={{
          bgcolor: 'transparent',
          width: 40,
          height: 40,
        }}
      >
        {config.icon}
      </Avatar>
      <Box flex={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          {config.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.instanceName}
        </Typography>
        {data.phone && (
          <Typography variant="caption" color="text.secondary">
            {data.phone}
          </Typography>
        )}
        <Box mt={0.5}>
          <Chip 
            label={data.message} 
            size="small" 
            color={config.chipColor}
            variant="outlined"
          />
        </Box>
      </Box>
    </Box>
  );
};

// Componente customizado para toast de QR Code
const QRCodeToast = ({ data }) => {
  const theme = useTheme();

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Avatar
        sx={{
          bgcolor: theme.palette.primary.main,
          width: 40,
          height: 40,
        }}
      >
        <QrCode sx={{ color: 'white' }} />
      </Avatar>
      <Box flex={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          QR Code Gerado!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.instanceName}
        </Typography>
        {data.pairingCode && (
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            Código: {data.pairingCode}
          </Typography>
        )}
        <Box mt={0.5}>
          <Chip 
            label="Escaneie para conectar" 
            size="small" 
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Hook para gerenciar notificações WhatsApp
 */
export const useWhatsAppNotifications = () => {
  const showConnectionNotification = (data) => {
    const { status, instanceName, phone, message } = data;
    
    const toastConfig = {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: status === 'open' ? 5000 : 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
    };

    switch (status) {
      case 'open':
        toast.success(
          <ConnectionStatusToast data={data} />,
          {
            ...toastConfig,
            autoClose: 6000, // Mais tempo para sucesso
          }
        );
        break;
      
      case 'close':
        toast.info(
          <ConnectionStatusToast data={data} />,
          toastConfig
        );
        break;
      
      case 'connecting':
        toast.warning(
          <ConnectionStatusToast data={data} />,
          {
            ...toastConfig,
            autoClose: false, // Não remove automaticamente
          }
        );
        break;
      
      case 'failed':
        toast.error(
          <ConnectionStatusToast data={data} />,
          {
            ...toastConfig,
            autoClose: 7000, // Mais tempo para erros
          }
        );
        break;
      
      default:
        toast.info(
          <ConnectionStatusToast data={data} />,
          toastConfig
        );
    }
  };

  const showQRCodeNotification = (data) => {
    toast.info(
      <QRCodeToast data={data} />,
      {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: false, // QR Code deve ficar até ser fechado manualmente
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        theme: "dark",
      }
    );
  };

  const showGenericNotification = (message, type = 'info') => {
    const config = {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
    };

    switch (type) {
      case 'success':
        toast.success(message, config);
        break;
      case 'error':
        toast.error(message, config);
        break;
      case 'warning':
        toast.warning(message, config);
        break;
      default:
        toast.info(message, config);
    }
  };

  return {
    showConnectionNotification,
    showQRCodeNotification,
    showGenericNotification,
  };
};

/**
 * Componente para processar notificações SSE automaticamente
 */
const WhatsAppNotifications = ({ notifications, onProcessed }) => {
  const { showConnectionNotification, showQRCodeNotification } = useWhatsAppNotifications();

  useEffect(() => {
    notifications.forEach((notification) => {
      const { data } = notification;
      
      switch (data.type) {
        case 'connection_status_changed':
          showConnectionNotification(data);
          break;
        
        case 'qr_code_generated':
          showQRCodeNotification(data);
          break;
        
        default:
          console.log('Tipo de notificação não reconhecido:', data.type);
      }
      
      // Marca como processada
      onProcessed(notification.id);
    });
  }, [notifications, showConnectionNotification, showQRCodeNotification, onProcessed]);

  return null; // Componente apenas para side effects
};

export default WhatsAppNotifications;
